import { Color, Spacing } from '@persona-id/design-tokens';
import { SidebarRightIcon } from '@persona-id/design-tokens/icons';
import { cloneDeep } from 'lodash';
import { noop } from 'lodash/fp';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { ValidateResult } from 'react-hook-form';
import styled, { css } from 'styled-components';

import {
  AlternativeModuleNames,
  ModuleTypes,
} from 'dashboard/apps/DashboardLayoutEditor/constants';
import { ModuleTypeInfo } from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/constants';
import ControlPanel from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/ControlPanel';
import { performTopCompaction } from 'dashboard/apps/DashboardLayoutEditor/gridPositioningUtils';
import LayoutCanvas from 'dashboard/apps/DashboardLayoutEditor/LayoutPreview/LayoutCanvas';
import { TabbedSection } from 'dashboard/components/TabbedSectionV2';
import EditorToolbar from 'dashboard/components-lib/EditorToolbar';
import ToolbarControlButton from 'dashboard/components-lib/ToolbarControlButton';
import { FULL_SCREEN_HEADER_HEIGHT } from 'dashboard/layouts/ShowViewLayoutV2';
import { BaseModule } from 'dashboard/lib/module';
import { generateTabToken } from 'dashboard/models/case-template';
import { Environment, EnvironmentName } from 'dashboard/models/environment';
import { Configuration, Tab } from 'dashboard/models/grid-layout';
import { Organization } from 'dashboard/models/organization';
import StorageClient, { LocalStorageKey } from 'lib/clients/StorageClient';
import useToggle from 'lib/hooks/useToggle';
import useUndoableUpdate from 'lib/hooks/useUndoableUpdate';
import { JsonSchemaObject } from 'lib/json-schema';

const EDITOR_TOOLBAR_HEIGHT = 49;

const EditorContainer = styled.div<{ showRightNavPanel: boolean }>`
  display: grid;
  grid-template-columns: ${({ showRightNavPanel }) => (showRightNavPanel ? '1fr 400px' : '1fr')};
  height: calc(100vh - ${FULL_SCREEN_HEADER_HEIGHT}px - ${EDITOR_TOOLBAR_HEIGHT}px);
`;

const PreviewBackground = styled.div`
  background-color: ${Color.CoreSlate900};
  padding: ${Spacing[5]};
  overflow-y: scroll;
`;

const PreviewContainer = styled.div`
  background-color: ${Color.BackgroundSecondary};
  border-radius: 8px;
  padding: ${Spacing[5]};
`;

export const MODULE_DATA_FOCUS_VALUE = 'draggable-grid-module';
export const LAYOUT_CANVAS_DATA_FOCUS_VALUE = 'layout-editor-canvas';

export type UpdateModule<M extends BaseModule> = (tabToken: string, module: M) => void;
export type UpdateLayout<M extends BaseModule> = (tabToken: string, modules: M[]) => void;
export type DeleteModule = (tabToken: string, moduleToken: string) => void;

export interface LayoutEditorProps<
  M extends BaseModule,
  T extends Tab<M>,
  SupportedModuleTypes extends ModuleTypes,
> {
  organization: Organization;
  currentEnvironment: Environment;
  layoutConfiguration: Configuration<M>;
  environmentName: EnvironmentName;
  dataSchema: JsonSchemaObject;
  shouldShowVisibilityToggle: boolean;
  shouldShowVisibilityAdvancedControls: boolean;
  shouldShowAdvancedOptions: boolean;
  supportedModuleTypes: Record<string, ModuleTypeInfo<SupportedModuleTypes>>;
  supportedComponentTypes: Record<string, ModuleTypeInfo<SupportedModuleTypes>>;
  generateDefaultModuleConfiguration: (moduleType: SupportedModuleTypes) => M;
  updateLayoutConfiguration: (layout: Configuration<M>) => void;
  setCaseTemplateIsValid?: (valid: boolean) => void;
  renderModule: (module: M) => ReactNode;
  renderModuleConfiguration: (module: M, handleUpdateModule: (module: M) => void) => ReactNode;
  renderTabForm: (
    tab: T,
    handleHideModal: () => void,
    handleEdit: (tab: T) => void,
    handleValidateTabName: (tabName: string, tabToken?: string) => ValidateResult,
  ) => ReactNode;
}

function LayoutEditor<
  M extends BaseModule,
  T extends Tab<M>,
  SupportedModuleTypes extends ModuleTypes,
>({
  organization,
  currentEnvironment,
  layoutConfiguration,
  dataSchema,
  environmentName,
  shouldShowVisibilityToggle,
  shouldShowVisibilityAdvancedControls,
  shouldShowAdvancedOptions,
  supportedModuleTypes,
  supportedComponentTypes,
  generateDefaultModuleConfiguration,
  updateLayoutConfiguration,
  setCaseTemplateIsValid = noop,
  renderModule,
  renderModuleConfiguration,
  renderTabForm,
}: LayoutEditorProps<M, T, SupportedModuleTypes>) {
  const [selectedModuleToken, setSelectedModuleToken] = useState<string | AlternativeModuleNames>(
    AlternativeModuleNames.ComponentLibrary,
  );
  const [showRightNavPanel, { toggle: toggleRightNavPanel }] = useToggle(true);

  const handleUpdateLayoutConfiguration = (nextLayout: Configuration<M>) => {
    // updated layout could lead to a deletion via undo/redo. If we can't find the module, we
    // need to reset the selectedModuleToken otherwise we'll run into a NPE.
    const currentModule = nextLayout.tabs
      .flatMap((tab) => tab.modules)
      .find((m) => m.token === selectedModuleToken);
    if (!currentModule) resetToComponentLibrary();

    updateLayoutConfiguration(nextLayout);
  };

  const {
    handleChangeWithUndoRedo: updateLayoutConfigurationWithUndo,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useUndoableUpdate(layoutConfiguration, handleUpdateLayoutConfiguration);

  const controls = {
    undo: {
      disabled: !canUndo,
      onClick: undo,
    },
    redo: {
      disabled: !canRedo,
      onClick: redo,
    },
  };

  const resetToComponentLibrary = () => {
    setSelectedModuleToken(AlternativeModuleNames.ComponentLibrary);
  };

  const addTab = (name: string) => {
    const nextCaseLayout = {
      ...layoutConfiguration,
      tabs: [
        ...layoutConfiguration.tabs,
        {
          token: generateTabToken(),
          name,
          modules: [],
        },
      ],
    };

    updateLayoutConfigurationWithUndo(nextCaseLayout);
  };

  const updateTabs = (tabs: Tab<M>[]) => {
    const nextCaseLayout = {
      ...layoutConfiguration,
      tabs: tabs,
    };
    updateLayoutConfigurationWithUndo(nextCaseLayout);
  };

  const modulesInfo = layoutConfiguration.tabs
    .map((tab) => {
      return tab.modules.map((module) => {
        return {
          tabToken: tab.token,
          module: module,
        };
      });
    })
    .flat();

  const updateModule: UpdateModule<M> = (tabToken, module) => {
    const nextCaseLayout = cloneDeep(layoutConfiguration);
    const tabIndex = layoutConfiguration.tabs.findIndex((tab) => tab.token === tabToken);
    const moduleIndex = layoutConfiguration.tabs[tabIndex].modules.findIndex(
      (m) => m.token === module.token,
    );

    nextCaseLayout.tabs[tabIndex].modules[moduleIndex] = module;

    updateLayoutConfigurationWithUndo(nextCaseLayout);
    setSelectedModuleToken(module.token);
  };

  const updateLayout: UpdateLayout<M> = (tabToken, modules) => {
    const nextCaseLayout = cloneDeep(layoutConfiguration);
    const tabIndex = layoutConfiguration.tabs.findIndex((tab) => tab.token === tabToken);

    nextCaseLayout.tabs[tabIndex].modules = modules;

    updateLayoutConfigurationWithUndo(nextCaseLayout);
  };

  const deleteModule: DeleteModule = useCallback(
    (tabToken, moduleToken) => {
      const nextCaseLayout = cloneDeep(layoutConfiguration);
      const tabIndex = nextCaseLayout.tabs.findIndex((tab) => tab.token === tabToken);
      const moduleIndex = nextCaseLayout.tabs[tabIndex].modules.findIndex(
        (module) => module.token === moduleToken,
      );
      nextCaseLayout.tabs[tabIndex].modules.splice(moduleIndex, 1);

      performTopCompaction(nextCaseLayout.tabs[tabIndex].modules);
      updateLayoutConfigurationWithUndo(nextCaseLayout);
    },
    [layoutConfiguration, updateLayoutConfigurationWithUndo],
  );

  useEffect(() => {
    const hotKeysEventHandler = (e: KeyboardEvent) => {
      if (e.key === 'z' && (e.ctrlKey || e.metaKey) && e.shiftKey) {
        redo();
      } else if (e.key === 'z' && (e.ctrlKey || e.metaKey)) {
        undo();
      }

      // copy and delete are scoped specifically to a focused DraggableGridModule
      // check if the event passed through DraggableGridModule with data-focus attribute
      const path = e.composedPath();
      const sourceDiv = path.find(
        (element) =>
          element instanceof HTMLElement &&
          element.getAttribute('data-focus') === MODULE_DATA_FOCUS_VALUE,
      );
      if (!sourceDiv) return;

      if (e.key === 'c' && (e.ctrlKey || e.metaKey)) {
        // find current module
        const currentModule = layoutConfiguration.tabs
          .flatMap((tab) => tab.modules)
          .find((m) => m.token === selectedModuleToken);

        StorageClient.setLocalItem(
          LocalStorageKey.DashboardLayoutEditorCopiedModule,
          JSON.stringify(currentModule),
        );
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        const currentTab = layoutConfiguration.tabs.find((tab) => {
          return tab.modules.some((m) => m.token === selectedModuleToken);
        });

        if (!currentTab) return;

        deleteModule(currentTab.token, selectedModuleToken);
      }
    };

    window.addEventListener('keydown', hotKeysEventHandler);

    return () => {
      window.removeEventListener('keydown', hotKeysEventHandler);
    };
  }, [layoutConfiguration, selectedModuleToken, deleteModule, undo, redo]);

  const tabs = layoutConfiguration.tabs.map((tab) => {
    return {
      name: tab.name,
      contents: (
        <LayoutCanvas
          tabToken={tab.token}
          currentModules={tab.modules}
          renderModule={renderModule}
          selectedModuleToken={selectedModuleToken}
          updateLayout={updateLayout}
          setSelectedModuleToken={setSelectedModuleToken}
          resetToComponentLibrary={resetToComponentLibrary}
        />
      ),
    };
  });

  return (
    <>
      <EditorToolbar
        additionalCss={css`
          border-left: none;
          border-top: none;
          border-right: none;
        `}
        id='layout-editor'
        controls={controls}
        rightNavItems={
          <ToolbarControlButton
            id='layout-editor__toggle-right-panel'
            tooltipText='Toggle right panel'
            active={showRightNavPanel}
            onClick={toggleRightNavPanel}
            icon={<SidebarRightIcon />}
          />
        }
      />
      <EditorContainer showRightNavPanel={showRightNavPanel}>
        <PreviewBackground>
          <PreviewContainer>
            <TabbedSection
              cardView={true}
              tabs={tabs}
              data-test={`layout-preview__tabs--${environmentName}`}
              onClick={() => setSelectedModuleToken(AlternativeModuleNames.Tabs)}
            />
          </PreviewContainer>
        </PreviewBackground>
        {showRightNavPanel && (
          <ControlPanel
            organization={organization}
            currentEnvironment={currentEnvironment}
            layoutConfiguration={layoutConfiguration}
            modulesInfo={modulesInfo}
            dataSchema={dataSchema}
            environmentName={environmentName}
            selectedModuleToken={selectedModuleToken}
            shouldShowVisibilityToggle={shouldShowVisibilityToggle}
            shouldShowVisibilityAdvancedControls={shouldShowVisibilityAdvancedControls}
            shouldShowAdvancedOptions={shouldShowAdvancedOptions}
            supportedModuleTypes={supportedModuleTypes}
            supportedComponentTypes={supportedComponentTypes}
            generateDefaultModuleConfiguration={generateDefaultModuleConfiguration}
            updateModule={updateModule}
            deleteModule={deleteModule}
            addTab={addTab}
            updateTabs={updateTabs}
            resetToComponentLibrary={resetToComponentLibrary}
            setCaseTemplateIsValid={setCaseTemplateIsValid}
            renderModuleConfiguration={renderModuleConfiguration}
            renderTabForm={renderTabForm}
          />
        )}
      </EditorContainer>
    </>
  );
}

export default LayoutEditor;
